import { BaseResponse } from 'domain/payload/response/BaseResponseDto';
import axiosInstance from '../config/AxiosConfig'
import { DashboardListResp } from 'domain/payload/response/dashboard/DashboardListResp';
import { DashboardDetailResp } from 'domain/payload/response/dashboard/DashboardDetailResp';
import { DashboardConfigResp } from 'domain/payload/response/dashboard/DashboardConfigResp';
import { DashboardDataDecisionReq } from 'domain/payload/request/dashboard-data/DashboardDataDecisionReq';
import { DashboardDecisionConfigResp } from 'domain/payload/response/dashboard/DashboardDecisionConfigResp';
import { FormConfigListResp } from 'domain/payload/response/dashboard/FormConfigListResp'; 
import { DashboardTribeConfigResp, TribeConfigListResp } from 'domain/payload/response/tribe/TribeResp';
import { DashboardChartResp } from 'domain/payload/response/dashboard/DashboardChartResp';
import { DashboardJobGroupConfigResp } from 'domain/payload/response/dashboard/JobGroupConfigResp';

const DashboardDataService = () => {
  const dashboardDataList = async (
    token: string,
    search: string | null,
    page: number | null,
    size: number | null,
    projectId: string | null,
    formId: string | null,
    tribeId: string | null,
    status: string | null,
    startDate: string | null,
    endDate: string | null,
    sortBy: string | null,
    sortOrder: string | null,
    notesFilter: string | null,
    isRunning?: boolean | null,
    signal?: AbortSignal,
    groupName?: string | null,
  ): Promise<BaseResponse<DashboardListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/dashboard-data/list`, {
        signal,
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          search: search,
          page: page,
          size: size,
          projectId: projectId,
          formId: formId,
          tribeId: tribeId,
          status: status,
          startDate: startDate,
          endDate: endDate,
          sortBy: sortBy,
          sortOrder: sortOrder,
          notesFilter: notesFilter,
          ...(isRunning !== null && isRunning !== undefined && { isRunning }),
          ...(groupName != null && groupName !== "" && { groupName })
        }
      });

      return response.data

    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || error?.message === 'canceled') {
        throw error;
      }
      throw error.response?.data?.message || error.message;
    }
  };

  const dashboardDataDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<DashboardDetailResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/dashboard-data/detail?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const dashboardDataConfig = async (
    token: string
  ): Promise<BaseResponse<DashboardConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/dashboard-data/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const dashboardDataFormConfig = async (
    token: string,
    projectId: string
  ): Promise<BaseResponse<FormConfigListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/dashboard-data/form-config`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const dashboarDataExportToCsv = async (
    token: string,
    projectId: string,
    startDate?: string | null,
    endDate?: string | null,
    status?: string,
    search?: string,
    formId?: string | null,
    tribeId?: string | null
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/dashboard-data/exportToCsv`, {}, { 
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
          startDate: startDate,
          endDate: endDate,
          status: status,
          search: search,
          formId: formId,
          tribeId: tribeId
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message || error;
    }
  };

  const dashboardDataDecision = async (
    token: string,
    request: DashboardDataDecisionReq
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/dashboard-data/decision`, request, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const dashboardDataUpdateValue = async (
    token: string,
    resultValueId: string,
    value: string,
    resultId?: string,
    fieldId?: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.put(`/v1/legacy/cms/dashboard-data/updateValue`, 
      {
        resultValueId: resultValueId,
        resultId: resultId,
        fieldId: fieldId,
        value: value
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const dashboardDataBackToQc = async (
    token: string,
    resultId: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.put(`/v1/legacy/cms/dashboard-data/backToQc`, 
        { resultId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("backToQc API response:", response.data);

      return response.data;
    } catch (error: any) {
      console.error("backToQc API error:", error.response?.data || error.message);
      throw error.response?.data?.message || error.message;
    }
  };

  const uploadFile = async (
    token: string,
    resultId: string,
    fieldId: string,
    file?: File | null
  ): Promise<BaseResponse<any>> => {
    try {
      const formData = new FormData();
      formData.append("resultId", resultId);
      formData.append("fieldId", fieldId);
      if (file) formData.append("file", file);

      const response = await axiosInstance.post(
        `/v1/legacy/cms/dashboard-data/upload-file`,
        formData,
        {
          headers: { 
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`
          },
        }
      );

      return response.data;
    } catch (error: any) {
      throw (
        error.response?.data?.message ||
        error.message ||
        "Gagal upload file"
      );
    }
  };

  const dashboardDataUpdateResultLocation = async (
    token: string,
    resultId: string,
    latitude: number,
    longitude: number,
    address?: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/dashboard-data/update-location`, 
      {
        resultId: resultId,
        latitude: latitude,
        longitude: longitude,
        address: address
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const dashboardDataUpdateNote = async (
    token: string,
    resultId: string,
    notes: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.put(`/v1/legacy/cms/dashboard-data/${resultId}/note`, 
      { notes }, 
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      return response.data

    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const dashboardDataDecisionConfig = async (
    token: string
  ): Promise<BaseResponse<DashboardDecisionConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/dashboard-data/decision/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const dashboardDataTribeConfig = async (
    token: string,
    projectId: string
  ): Promise<BaseResponse<DashboardTribeConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/dashboard-data/tribe-config`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const dashboardDataJobGroupConfig = async (
    token: string,
    projectId: string
  ): Promise<BaseResponse<DashboardJobGroupConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/dashboard-data/job-group-config`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
        }
      });

      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const dashboardDataChart = async (
    token: string,
    search: string | null,
    projectId: string | null,
    formId: string | null,
    tribeId: string | null,
    status: string | null,
    startDate: string | null,
    endDate: string | null,
    notesFilter: string | null,
    isRunning?: boolean | null,
    signal?: AbortSignal,
    groupName?: string | null,
  ): Promise<BaseResponse<DashboardChartResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/dashboard-data/chart`, {
        signal,
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search,
          projectId,
          formId,
          tribeId,
          status,
          startDate,
          endDate,
          notesFilter,
          ...(isRunning !== null && isRunning !== undefined && { isRunning }),
          ...(groupName != null && groupName !== "" && { groupName })
        }
      });
      return response.data
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || error?.message === 'canceled') {
        throw error;
      }
      throw error.response?.data?.message || error.message;
    }
  };

  const updateMissRoutingRestriction = async (
    token: string,
    resultId: string,
    isRestrictMissRouting: boolean
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.put(
        `/v1/legacy/cms/dashboard-data/update-miss-routing-restriction`,
        {
          resultId,
          isRestrictMissRouting,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  return { 
    dashboardDataList, 
    dashboardDataDetail, 
    dashboardDataConfig, 
    dashboardDataFormConfig,
    dashboarDataExportToCsv, 
    dashboardDataDecision, 
    dashboardDataUpdateValue, 
    dashboardDataBackToQc, 
    uploadFile, 
    dashboardDataUpdateResultLocation, 
    dashboardDataUpdateNote,
    updateMissRoutingRestriction,
    dashboardDataDecisionConfig,
    dashboardDataTribeConfig,
    dashboardDataJobGroupConfig,
    dashboardDataChart
  };
};

export default DashboardDataService;